import { Button, Radio } from '@apollo/orbit';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DATETIME_LOCAL_FORMAT_STR } from 'src/components/timeRangeFilter/TimeRangeFilter';
import {
  DateFilterOption,
  DateSelectedFn,
  dateFilterOptionLabels,
} from 'src/lib/config/dateFilterOptions';

export const DateFilter = ({
  className,
  option: selectedOption,
  options,
  date,
  onDateSelected,
}: {
  className?: string;
  option: DateFilterOption;
  options: ReadonlyArray<DateFilterOption>;
  date: Date;
  onDateSelected: DateSelectedFn;
}) => {
  const [updatedDate, setUpdatedDate] = useState<Date>(date);
  const [customPickerError, setCustomPickerError] = useState<string>();
  const lastOption = useRef<DateFilterOption>(selectedOption);

  useEffect(() => {
    if (lastOption.current !== selectedOption) {
      lastOption.current = selectedOption;
      setUpdatedDate(date);
    }
  }, [selectedOption, date]);

  const shouldShowDatePicker =
    options.includes('custom') && selectedOption === 'custom';

  const customOptionDirty = date.valueOf() !== updatedDate.valueOf();

  const handleChange = useCallback(
    (newDate: Date, option: DateFilterOption) => {
      if (option !== 'custom') {
        onDateSelected(option, undefined);
      } else {
        onDateSelected(option, newDate.toISOString());
      }
    },
    [onDateSelected],
  );

  return (
    <div className={className}>
      {options.map((option) => {
        return (
          <div
            key={option}
            className="-mx-2 flex cursor-pointer items-center rounded px-2 py-1 hover:bg-secondary"
            onClick={() => handleChange(date, option)}
          >
            <Radio isChecked={selectedOption === option} />
            <span className="ml-2 flex-1">
              {dateFilterOptionLabels[option]}
            </span>
          </div>
        );
      })}

      {shouldShowDatePicker && (
        <>
          <input
            data-testid="custom-date-input"
            defaultValue={moment(date).format(DATETIME_LOCAL_FORMAT_STR)}
            onChange={(e) => {
              const newUpdatedDate = new Date(e.target.value);
              if (!e.target.validity.valid) {
                setCustomPickerError(e.target.validationMessage);
                if (!isNaN(newUpdatedDate.valueOf())) {
                  // only update our react state if the input is a valid date
                  setUpdatedDate(newUpdatedDate);
                }
              } else {
                setCustomPickerError(undefined);
                setUpdatedDate(newUpdatedDate);
              }
            }}
            type="date"
            className="w-full rounded border border-primary px-2 hover:border-blue"
            max={moment().format(DATETIME_LOCAL_FORMAT_STR)}
          />
          {customPickerError && (
            <div className="mt-2 leading-tight text-red">
              {customPickerError}
            </div>
          )}
          {customOptionDirty && !customPickerError && (
            <Button
              size="sm"
              className="mt-4"
              onClick={() => {
                handleChange(updatedDate, 'custom');
              }}
            >
              Update
            </Button>
          )}
        </>
      )}
    </div>
  );
};
